import React, { useEffect, useState } from "react";

import Select from "../ReusableComponents/Select";
import { DEFAULT_ACCURACY_CONFIG, metricsForMode } from "../../utils/forecastAccuracy";
import { describeForecastOutput, primaryParameter } from "../../utils/seriesMetaData";
import type { SeriesParameter } from "../../utils/seriesMetaData";
import type { AccuracyMetricId, AccuracyMode } from "../../utils/forecastAccuracy";
import type { SourceAccuracySettings } from "../../types/composedForecast";

interface ObservationAccuracyConfigProps {
  settings: SourceAccuracySettings | null | undefined;
  onChange: (settings: SourceAccuracySettings | null) => void;
  /** The base model's output — its seriesMetaData narrows the trace and band pickers. */
  baseOutputId?: string;
}

// Traces a forecast commonly carries — the full list when the base model's contents aren't known yet.
const TRACE_OPTIONS = [
  { value: "mean", label: "Mean" },
  { value: "p50", label: "Median (p50)" },
  { value: "control", label: "Control member" },
  { value: "value", label: "Single trace (deterministic)" },
  ...[5, 10, 25, 75, 90, 95].map((p) => ({ value: `p${p}`, label: `p${p}` })),
];

const BAND_OPTIONS: Array<[number, number]> = [
  [5, 95],
  [10, 90],
  [25, 75],
  [40, 60],
];

/** Only the traces and bands the forecast really has; everything when its contents are unknown. */
export const availableOptions = (param: SeriesParameter | null) => {
  if (!param) return { traces: TRACE_OPTIONS, bands: BAND_OPTIONS };
  const { kinds } = param;
  const traces = TRACE_OPTIONS.filter((o) => {
    if (o.value === "mean") return kinds.mean;
    if (o.value === "control") return kinds.control;
    if (o.value === "value") return kinds.value;
    return kinds.percentiles.includes(Number(o.value.slice(1)));
  });
  // Percentiles the generic list doesn't name (p40, p60 …) are still offered
  kinds.percentiles
    .filter((p) => !traces.some((o) => o.value === `p${p}`))
    .forEach((p) => traces.push({ value: `p${p}`, label: `p${p}` }));
  const bands = kinds.percentiles
    .filter((p) => p < 50 && kinds.percentiles.includes(100 - p))
    .map((p) => [p, 100 - p] as [number, number]);
  return { traces, bands };
};

export const describeKinds = (param: SeriesParameter) => {
  const parts: string[] = [];
  if (param.kinds.mean) parts.push("mean");
  if (param.kinds.control) parts.push("control");
  if (param.kinds.value) parts.push("a single trace");
  if (param.kinds.percentiles.length) parts.push(`p${param.kinds.percentiles.join("/")}`);
  if (param.kinds.members) parts.push(`${param.kinds.members} members (percentiles computed from them)`);
  return parts.join(", ") || "no recognised series";
};

/** "Use as observation": score the composed forecast's base model against this source. */
const ObservationAccuracyConfig: React.FC<ObservationAccuracyConfigProps> = ({ settings, onChange, baseOutputId }) => {
  const enabled = !!settings?.enabled;
  const mode: AccuracyMode = settings?.mode ?? "single";
  const [baseParam, setBaseParam] = useState<SeriesParameter | null>(null);

  useEffect(() => {
    setBaseParam(null);
    if (!enabled || !baseOutputId) return;
    let cancelled = false;
    describeForecastOutput(baseOutputId)
      .then((meta) => {
        if (!cancelled) setBaseParam(primaryParameter(meta));
      })
      .catch((err) => console.error("Could not describe the forecast output:", err));
    return () => {
      cancelled = true;
    };
  }, [enabled, baseOutputId]);

  // Members-only forecasts are scored on percentiles computed from the members (spec §7)
  const options = availableOptions(
    baseParam && baseParam.kinds.members > 0 && !baseParam.kinds.percentiles.length
      ? { ...baseParam, kinds: { ...baseParam.kinds, percentiles: [10, 25, 50, 75, 90] } }
      : baseParam
  );

  // A saved trace/band the forecast no longer has (or a default it never had) moves to the first that exists.
  // Deliberately keyed on [baseParam, mode] only: it should run when the forecast's contents arrive or the mode
  // changes, not on every settings edit. The switch shows in the form before saving (nothing is written until
  // the user saves), so an outdated choice can't be scored silently.
  useEffect(() => {
    if (!baseParam || !enabled || !settings) return;
    if (mode === "single" && options.traces.length && !options.traces.some((o) => o.value === settings.series)) {
      onChange({ ...settings, series: options.traces[0].value });
    }
    const band = (settings.band ?? []).join("-");
    if (mode === "band" && options.bands.length && !options.bands.some((b) => b.join("-") === band)) {
      onChange({ ...settings, band: options.bands[options.bands.length - 1] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseParam, mode]);

  const setMode = (next: AccuracyMode) => onChange({ ...DEFAULT_ACCURACY_CONFIG[next], enabled: true });
  const update = (patch: Partial<SourceAccuracySettings>) =>
    onChange({ ...(settings as SourceAccuracySettings), ...patch });
  const toggleMetric = (id: AccuracyMetricId) => {
    const metrics = settings?.metrics ?? [];
    update({ metrics: metrics.includes(id) ? metrics.filter((m) => m !== id) : [...metrics, id] });
  };

  return (
    <div className="space-y-3">
      <label className="flex cursor-pointer items-start gap-2">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={enabled}
          onChange={(e) => onChange(e.target.checked ? { ...DEFAULT_ACCURACY_CONFIG.single, enabled: true } : null)}
        />
        <span>
          <span className="eai-meta-label block">Use as observation</span>
          <span className="font-inter text-eai-body-xs text-eai-ink-subtle">
            Score the forecast (the first model output) against this source for every forecast issue.
          </span>
        </span>
      </label>

      {enabled && baseParam && (
        <p className="font-inter text-eai-body-xs text-eai-ink-subtle">
          The forecast has {describeKinds(baseParam)}
          {baseParam.unit ? ` in ${baseParam.unit}` : ""}.
        </p>
      )}

      {enabled && settings && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="eai-meta-label mb-2 block">Compare</label>
            <Select value={mode} onChange={(e) => setMode(e.target.value as AccuracyMode)} containerClassName="w-full">
              <option value="single">A single trace</option>
              {(!baseParam || options.bands.length > 0) && <option value="band">A percentile band</option>}
            </Select>
          </div>

          {mode === "single" && options.traces.length === 0 ? (
            <div className="font-inter text-eai-body-xs text-eai-ink-subtle">
              The forecast has no trace this can compare (no mean, percentile, control or single value).
            </div>
          ) : mode === "single" ? (
            <div>
              <label className="eai-meta-label mb-2 block">Forecast trace</label>
              <Select
                value={settings.series ?? "mean"}
                onChange={(e) => update({ series: e.target.value })}
                containerClassName="w-full"
              >
                {options.traces.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
          ) : (
            <div>
              <label className="eai-meta-label mb-2 block">Band</label>
              <Select
                value={(settings.band ?? [25, 75]).join("-")}
                onChange={(e) => update({ band: e.target.value.split("-").map(Number) as [number, number] })}
                containerClassName="w-full"
              >
                {options.bands.map(([lo, hi]) => (
                  <option key={lo} value={`${lo}-${hi}`}>
                    p{lo}–p{hi} ({hi - lo}% interval)
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div>
            <label className="eai-meta-label mb-2 block">Plot on right axis</label>
            <Select
              value={settings.plotMetric ?? ""}
              onChange={(e) => update({ plotMetric: (e.target.value || undefined) as AccuracyMetricId | undefined })}
              containerClassName="w-full"
            >
              <option value="">Nothing</option>
              {metricsForMode(mode, "timestep").map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="sm:col-span-3">
            <span className="eai-meta-label mb-2 block">Metrics</span>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {metricsForMode(mode, "summary").map((m) => (
                <label key={m.id} className="flex cursor-pointer items-center gap-1.5" title={m.description}>
                  <input
                    type="checkbox"
                    checked={settings.metrics.includes(m.id)}
                    onChange={() => toggleMetric(m.id)}
                  />
                  <span className="font-inter text-eai-body-sm text-eai-ink">{m.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObservationAccuracyConfig;
